import React, { useState, useEffect, useContext, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../contexts";
import { PlusIcon, PencilIcon, TrashIcon, CaretDownIcon, XIcon, ArrowRightIcon, UsersThreeIcon } from "@phosphor-icons/react";
import { CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import { toastOptions } from "../../../assets/constants";
import { CustomSearchInput, CustomTag, CustomModal, CustomPageHeader } from "../../../ui";

const Groups = () => {
    const { token } = useContext(AuthContext);
    const navigate = useNavigate();
    const [groups, setGroups] = useState([]);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedParent, setSelectedParent] = useState(null);
    const [selectedChildren, setSelectedChildren] = useState([]);
    const [groupName, setGroupName] = useState("");
    const [notes, setNotes] = useState("");
    const [editingGroup, setEditingGroup] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [closingGroups, setClosingGroups] = useState(new Set());
    const [allCustomers, setAllCustomers] = useState([]);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const fetchGroups = useCallback(async () => {
        try {
            setIsRefreshing(true);
            const [groupsResponse, customersResponse] = await Promise.all([
                axios.get(`${process.env.REACT_APP_UPA_API_HOST}grouped-support/groups`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
                axios.get(`${process.env.REACT_APP_UPA_API_HOST}pennylane/customers`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
            ]);

            setGroups(groupsResponse.data);
            setAllCustomers(customersResponse.data);
            setIsLoaded(true);
        } catch (error) {
            toast.error(`Erreur lors du chargement des groupes: ${error.response?.data?.details || error.message}`, toastOptions);
            setIsLoaded(true);
        } finally {
            setIsRefreshing(false);
        }
    }, [token]);

    useEffect(() => {
        fetchGroups();
    }, [fetchGroups]);

    const confirmRemoveChild = async () => {
        try {
            await axios.delete(`${process.env.REACT_APP_UPA_API_HOST}grouped-support/groups/${deleteTarget.relationId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            toast.success("Enfant retiré du groupe avec succès", toastOptions);
            fetchGroups();
            window.dispatchEvent(new CustomEvent("groupedSupportUpdated"));
        } catch (error) {
            toast.error(`Erreur lors de la suppression: ${error.response?.data?.error || error.message}`, toastOptions);
        } finally {
            setDeleteModalOpen(false);
            setDeleteTarget(null);
        }
    };

    const handleRemoveGroup = (group) => {
        setDeleteTarget({ type: "group", group });
        setDeleteModalOpen(true);
    };

    const confirmRemoveGroup = async () => {
        try {
            const deletePromises = deleteTarget.group.children.map((child) =>
                axios.delete(`${process.env.REACT_APP_UPA_API_HOST}grouped-support/groups/${child.relationId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                })
            );

            await Promise.all(deletePromises);
            toast.success("Groupe supprimé avec succès", toastOptions);
            fetchGroups();
            window.dispatchEvent(new CustomEvent("groupedSupportUpdated"));
        } catch (error) {
            toast.error(`Erreur lors de la suppression: ${error.response?.data?.error || error.message}`, toastOptions);
        } finally {
            setDeleteModalOpen(false);
            setDeleteTarget(null);
        }
    };

    const handleClearCache = async () => {
        try {
            setIsRefreshing(true);
            await axios.delete(`${process.env.REACT_APP_UPA_API_HOST}grouped-support/cache`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast.success("Cache vidé, recalcul en cours...", toastOptions);
            fetchGroups();
        } catch (error) {
            toast.error(`Erreur lors de la suppression du cache: ${error.response?.data?.error || error.message}`, toastOptions);
        }
    };

    const filteredGroups = groups.filter(
        (group) =>
            group.parentName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            group.children.some((child) => child.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (group.groupName && group.groupName.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const handleCreateGroup = async () => {
        if (!selectedParent || selectedChildren.length === 0) {
            toast.error("Veuillez sélectionner un parent et au moins un enfant", toastOptions);
            return;
        }

        setIsCreating(true);
        try {
            if (isEditing && editingGroup) {
                await axios.put(
                    `${process.env.REACT_APP_UPA_API_HOST}grouped-support/groups/${editingGroup.id}`,
                    {
                        groupName: groupName || null,
                        notes: notes || null,
                    },
                    {
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );

                const existingChildIds = editingGroup.children.map((child) => child.id);
                const selectedChildIds = selectedChildren.map((child) => child.id);

                const childrenToAdd = selectedChildren.filter((child) => !existingChildIds.includes(child.id));

                const childrenToRemove = editingGroup.children.filter((child) => !selectedChildIds.includes(child.id));

                if (childrenToAdd.length > 0) {
                    const addPromises = childrenToAdd.map((child) =>
                        axios.post(
                            `${process.env.REACT_APP_UPA_API_HOST}grouped-support/groups`,
                            {
                                parentCustomerId: selectedParent.id,
                                childCustomerId: child.id,
                                groupName: groupName || null,
                                notes: notes || null,
                            },
                            {
                                headers: { Authorization: `Bearer ${token}` },
                            }
                        )
                    );
                    await Promise.all(addPromises);
                }

                if (childrenToRemove.length > 0) {
                    const removePromises = childrenToRemove.map((child) =>
                        axios.delete(`${process.env.REACT_APP_UPA_API_HOST}grouped-support/groups/${child.relationId}`, {
                            headers: { Authorization: `Bearer ${token}` },
                        })
                    );
                    await Promise.all(removePromises);
                }

                toast.success("Groupe modifié avec succès", toastOptions);
            } else {
                const createPromises = selectedChildren.map((child) =>
                    axios.post(
                        `${process.env.REACT_APP_UPA_API_HOST}grouped-support/groups`,
                        {
                            parentCustomerId: selectedParent.id,
                            childCustomerId: child.id,
                            groupName: groupName || null,
                            notes: notes || null,
                        },
                        {
                            headers: { Authorization: `Bearer ${token}` },
                        }
                    )
                );

                await Promise.all(createPromises);
                toast.success(`Groupe créé avec ${selectedChildren.length} enfant(s)`, toastOptions);
            }

            setIsModalOpen(false);
            setSelectedParent(null);
            setSelectedChildren([]);
            setGroupName("");
            setNotes("");
            setEditingGroup(null);
            setIsEditing(false);
            fetchGroups();
            window.dispatchEvent(new CustomEvent("groupedSupportUpdated"));
        } catch (error) {
            toast.error(`Erreur lors de ${isEditing ? "la modification" : "la création"}: ${error.response?.data?.error || error.message}`, toastOptions);
        } finally {
            setIsCreating(false);
        }
    };

    const handleOpenModal = () => {
        setSelectedParent(null);
        setSelectedChildren([]);
        setGroupName("");
        setNotes("");
        setEditingGroup(null);
        setIsEditing(false);
        setIsModalOpen(true);
    };

    const handleEditGroup = (group) => {
        setSelectedParent({ id: group.parentId, name: group.parentName });
        setSelectedChildren(group.children.map((child) => ({ id: child.id, name: child.name })));
        setGroupName(group.groupName || "");
        setNotes(group.notes || "");
        setEditingGroup(group);
        setIsEditing(true);
        setIsModalOpen(true);
    };

    const handleAddChild = (child) => {
        if (!selectedChildren.find((c) => c.id === child.id)) {
            setSelectedChildren([...selectedChildren, child]);
        }
    };

    const handleRemoveSelectedChild = (childId) => {
        setSelectedChildren(selectedChildren.filter((child) => child.id !== childId));
    };

    const toggleGroupExpansion = (groupId) => {
        if (expandedGroups.has(groupId)) {
            setClosingGroups((prev) => new Set(prev).add(groupId));
            setTimeout(() => {
                setExpandedGroups((prev) => {
                    const newExpanded = new Set(prev);
                    newExpanded.delete(groupId);
                    return newExpanded;
                });
                setClosingGroups((prev) => {
                    const newClosing = new Set(prev);
                    newClosing.delete(groupId);
                    return newClosing;
                });
            }, 200);
        } else {
            setExpandedGroups((prev) => new Set(prev).add(groupId));
        }
    };

    const getFilteredCustomers = (allCustomers) => {
        if (!allCustomers) return [];

        const excludedIds = new Set();

        if (selectedParent) {
            excludedIds.add(selectedParent.id);
        }

        selectedChildren.forEach((child) => {
            excludedIds.add(child.id);
        });

        groups.forEach((group) => {
            excludedIds.add(group.parentId);
            group.children.forEach((child) => {
                excludedIds.add(child.id);
            });
        });

        return allCustomers.filter((customer) => !excludedIds.has(customer.id));
    };

    const groupsCols = React.useMemo(
        () => [
            {
                Header: "Groupe",
                accessor: "groupName",
                icon: "tag",
                Cell: ({ cell: { value }, row: { original } }) => (
                    <div className="group-info">
                        <CustomTag value={value || `Groupe #${original.id}`} className="purple" />
                        <CustomTag value={`${original.children.length} enfant${original.children.length > 1 ? "s" : ""}`} className="blue small" />
                    </div>
                ),
            },
            {
                Header: "Client parent",
                accessor: "parentName",
                icon: "building",
                Cell: ({ cell: { value }, row: { original } }) => (
                    <div className="parent-info">
                        <CustomTag value={value} className="green" />
                        <CustomTag value={`${original.parentSupportHours}h disponibles`} className="green small" />
                    </div>
                ),
            },
            {
                Header: "Clients enfants",
                accessor: "children",
                icon: "users",
                Cell: ({ cell: { value } }) => (
                    <div className="children-info">
                        {value.slice(0, 2).map((child, index) => (
                            <CustomTag key={child.id} value={child.name} className="blue small" />
                        ))}
                        {value.length > 2 && <CustomTag value={`+${value.length - 2} autres`} className="deactivated small" />}
                    </div>
                ),
            },
            {
                Header: "Heures consommées",
                accessor: "totalConsumedHours",
                icon: "clock",
                Cell: ({ cell: { value }, row: { original } }) => {
                    const percentage = original.parentSupportHours ? (value / original.parentSupportHours) * 100 : 0;
                    const className = percentage > 90 ? "red" : percentage > 70 ? "orange" : "blue";
                    return (
                        <div className="hours-info">
                            <CustomTag value={`${value.toFixed(1)}h / ${original.parentSupportHours}h`} className={className} />
                            <CustomTag value={`${percentage.toFixed(0)}%`} className={`${className} small`} />
                        </div>
                    );
                },
            },
            {
                Header: "Notes",
                accessor: "notes",
                icon: "document",
                Cell: ({ cell: { value } }) => <CustomTag value={value || "Aucune note"} className={value ? "blue small" : "deactivated small"} />,
            },
            {
                Header: "Actions",
                accessor: "id",
                icon: "gear",
                Cell: ({ cell: { value }, row: { original } }) => (
                    <div className="table-actions">
                        <button className="button-large white-button small" onClick={() => handleEditGroup(original)} title="Modifier le groupe">
                            <PencilIcon size={14} />
                        </button>
                        <button className="button-large red-button small" onClick={() => handleRemoveGroup(original)} title="Supprimer le groupe">
                            <TrashIcon size={14} />
                        </button>
                    </div>
                ),
            },
        ],
        [handleEditGroup, handleRemoveGroup]
    );

    return isLoaded ? (
        <div className="c-app__view">
            <CustomPageHeader 
                title="Support mutualisé"
                titleIcon={{
                    icon: UsersThreeIcon,
                }}
                buttons={[
                    {
                        label: "Créer un groupe",
                        onClick: handleOpenModal,
                        variant: "primary",
                        icon: PlusIcon
                    }
                ]}
            />

            {filteredGroups.length > 0 ? (
                <div className="support-groups">
                    <div className="cards-container">
                        <div className="cards-box">
                            {filteredGroups.map((group, index) => {
                                const consumptionPercentage = group.parentSupportHours ? (group.totalConsumedHours / group.parentSupportHours) * 100 : 0;

                                const isOverLimit = consumptionPercentage > 100;

                                const getCardStatusClass = () => {
                                    return isOverLimit ? "c-card--danger" : "c-card--success";
                                };

                                const getStatusIndicatorClass = () => {
                                    return isOverLimit ? "c-card__status-indicator--danger" : "c-card__status-indicator--success";
                                };

                                return (
                                    <div key={group.id} className={`c-card ${getCardStatusClass()}`}>
                                        <div className="c-card__content">
                                            <div className="c-card__header">
                                                <div className="c-card__title-section">
                                                    <div className={`c-card__status-indicator ${getStatusIndicatorClass()}`}></div>
                                                    <h3 className="c-card__text">
                                                        <span className="c-card__title">{group.groupName || `Groupe #${index + 1}`}</span>
                                                    </h3>
                                                </div>
                                                <div className="c-card__info">
                                                    <div className="c-card__tag">
                                                        <CustomTag
                                                            value={`${(group.totalConsumedHours || 0).toFixed(1)}h / ${group.parentSupportHours || 0}h`}
                                                            className={isOverLimit ? "false" : "blue"}
                                                        />
                                                    </div>
                                                    <div className="c-card__actions">
                                                        <button className="c-card__action c-card__action--edit" onClick={() => handleEditGroup(group)} title="Modifier le groupe">
                                                            <PencilIcon size={14} />
                                                        </button>
                                                        <button className="c-card__action c-card__action--delete" onClick={() => handleRemoveGroup(group)} title="Supprimer le groupe">
                                                            <TrashIcon size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="c-card__main" onClick={() => navigate(`/admin/inventory/customers/${group.parentId}`)}>
                                                <div className="c-card__main-icon">
                                                    <img src={group.parentLogo || "https://image.store.altelis.com/images/altelis-tools/logo-altelis.png"} alt="parent-logo" />
                                                </div>
                                                <div className="c-card__main-info">
                                                    <p className="c-card__main-name">{group.parentName}</p>
                                                </div>
                                                <div className="c-card__main-tag">
                                                    <CustomTag value={`${(group.parentConsumedHours || 0).toFixed(1)}h`} />
                                                </div>
                                                <div className="c-card__main-link">
                                                    <ArrowRightIcon size={16} weight="bold" />
                                                </div>
                                            </div>

                                            <div className="c-card__details">
                                                <p className="c-card__details-title">
                                                    Client{group.children.length > 1 ? "s" : ""} enfant{group.children.length > 1 ? "s" : ""} ({group.children.length})
                                                </p>
                                                {group.children.slice(0, 2).map((child) => (
                                                    <div key={child.id} className="c-card__detail-item" onClick={() => navigate(`/admin/inventory/customers/${child.id}`)}>
                                                        <div className="c-card__detail-item-info">
                                                            <span className="c-card__detail-item-name">{child.name}</span>
                                                            {child.isPremium && <CustomTag value={`Premium (${child.premiumHours}h)`} className="purple uppercase small" />}
                                                            {child.isEssential && !child.isPremium && <CustomTag value="Essentiel" className="uppercase small" />}
                                                            {!child.isPremium && !child.isEssential && <CustomTag value="Hérite" className="premium uppercase small" />}
                                                        </div>
                                                        <div className="c-card__detail-item-tag">
                                                            <CustomTag value={`${(child.consumedHours || 0).toFixed(1)}h`} />
                                                        </div>
                                                        <div className="c-card__detail-item-link">
                                                            <ArrowRightIcon size={16} weight="bold" />
                                                        </div>
                                                    </div>
                                                ))}
                                                {group.children.length > 2 && (
                                                    <>
                                                        {(expandedGroups.has(group.id) || closingGroups.has(group.id)) && (
                                                            <div className={`c-card__dropdown ${closingGroups.has(group.id) ? "c-card__dropdown--closing" : ""}`}>
                                                                {group.children.slice(2).map((child) => (
                                                                    <div key={child.id} className="c-card__detail-item" onClick={() => navigate(`/admin/inventory/customers/${child.id}`)}>
                                                                        <div className="c-card__detail-item-info">
                                                                            <span className="c-card__detail-item-name">{child.name}</span>
                                                                            {child.isPremium && <CustomTag value={`Premium (${child.premiumHours}h)`} className="purple uppercase small" />}
                                                                            {child.isEssential && !child.isPremium && <CustomTag value="Essentiel" className="uppercase small" />}
                                                                            {!child.isPremium && !child.isEssential && <CustomTag value="Hérite" className="premium uppercase small" />}
                                                                        </div>
                                                                        <div className="c-card__detail-item-tag">
                                                                            <CustomTag value={`${(child.consumedHours || 0).toFixed(1)}h`} />
                                                                        </div>
                                                                        <div className="c-card__detail-item-link">
                                                                            <ArrowRightIcon size={16} weight="bold" />
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        <button className="c-card__expand-button" onClick={() => toggleGroupExpansion(group.id)}>
                                                            <span>{expandedGroups.has(group.id) ? "Masquer les enfants" : `+${group.children.length - 2} autres enfants`}</span>
                                                            <CaretDownIcon
                                                                size={12}
                                                                weight="bold"
                                                                style={{ transform: expandedGroups.has(group.id) ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }}
                                                            />
                                                        </button>
                                                    </>
                                                )}
                                            </div>

                                            {group.notes && (
                                                <>
                                                    <div className="c-card__divider"></div>
                                                    <p className="c-card__notes">Notes : {group.notes}</p>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="no-data-container">
                    <p>Aucun groupe de support trouvé</p>
                    <button className="button-large blue-button" onClick={handleOpenModal}>
                        <PlusIcon size={16} weight="bold" />
                        <span>Créer un groupe</span>
                    </button>
                </div>
            )}

            <CustomModal show={isModalOpen} setShow={setIsModalOpen} title={isEditing ? "Modifier un groupe de support" : "Nouveau groupe de support"}>
                <div className="custom__modal-group">
                    <label className="name">Nom du groupe (optionnel)</label>
                    <input type="text" placeholder="Nom du groupe..." value={groupName} onChange={(e) => setGroupName(e.target.value.toUpperCase())} className="classic-input" />
                </div>

                {!isEditing && (
                    <>
                        <div className="custom__modal-group">
                            <label className="project">Client parent</label>
                            <CustomSearchInput
                                placeholder="Rechercher un client parent..."
                                entity={getFilteredCustomers(allCustomers)}
                                onChange={setSelectedParent}
                                value={selectedParent}
                                labelType="customer"
                                isClearable
                            />
                        </div>

                        <div className="custom__modal-group">
                            <label className="tag-simple">Clients enfants</label>
                            <CustomSearchInput
                                placeholder="Rechercher et ajouter des clients enfants..."
                                entity={getFilteredCustomers(allCustomers)}
                                onChange={handleAddChild}
                                value={null}
                                labelType="customer"
                                isClearable
                            />
                            {selectedChildren.length > 0 && (
                                <div className="modal__selected-children">
                                    {selectedChildren.map((child, index) => (
                                        <div key={child.id} className="modal__selected-child">
                                            <CustomTag value={child.name} />
                                            <button type="button" onClick={() => handleRemoveSelectedChild(child.id)} className="modal__remove-child-btn">
                                                <XIcon size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}

                {isEditing && (
                    <>
                        <div className="custom__modal-group">
                            <label className="project">Client parent</label>
                            <input type="text" value={selectedParent?.name || ""} className="classic-input classic-input--disabled" disabled />
                        </div>

                        <div className="custom__modal-group">
                            <label className="tag-simple">Clients enfants</label>
                            <CustomSearchInput
                                placeholder="Rechercher et ajouter des clients enfants..."
                                entity={getFilteredCustomers(allCustomers)}
                                onChange={handleAddChild}
                                value={null}
                                labelType="customer"
                                isClearable
                                menuPlacement="bottom"
                            />
                            {selectedChildren.length > 0 && (
                                <div className="modal__selected-children">
                                    {selectedChildren.map((child, index) => (
                                        <div key={child.id} className="modal__selected-child">
                                            <CustomTag value={child.name} />
                                            <button type="button" onClick={() => handleRemoveSelectedChild(child.id)} className="modal__remove-child-btn">
                                                <XIcon size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}

                <div className="custom__modal-group">
                    <label className="comments">Notes (optionnel)</label>
                    <textarea placeholder="Notes sur ce groupe..." value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="classic-input classic-input--textarea" />
                </div>

                <div className="modal-buttons">
                    <button onClick={() => setIsModalOpen(false)} className="button-large cancel-button" disabled={isCreating}>
                        Annuler
                    </button>
                    <button onClick={handleCreateGroup} className="button-large green-button validate" disabled={isCreating || !selectedParent || selectedChildren.length === 0}>
                        {isCreating ? <CircularProgress size={16} className="modal-progress" /> : isEditing ? <PencilIcon size={16} /> : <PlusIcon size={16} />}
                        <span>{isCreating ? (isEditing ? "Modification..." : "Création...") : isEditing ? "Modifier le groupe" : "Enregistrer et créer"}</span>
                    </button>
                </div>
            </CustomModal>

            <CustomModal show={deleteModalOpen} setShow={setDeleteModalOpen} title="Confirmer la suppression">
                <div className="c-modal-content is-center">
                    {deleteTarget?.type === "child" ? (
                        <p>
                            Êtes-vous sûr de vouloir retirer <strong>"{deleteTarget.childName}"</strong> du groupe de <strong>"{deleteTarget.parentName}"</strong> ?
                        </p>
                    ) : (
                        <p>
                            Êtes-vous sûr de vouloir supprimer complètement le groupe <strong>"{deleteTarget?.group?.groupName || "sans nom"}"</strong> avec tous ses enfants ?
                        </p>
                    )}
                    <p>Cette action est irréversible.</p>
                </div>
                <div className="modal-buttons">
                    <button onClick={() => setDeleteModalOpen(false)} className="button-large cancel-button">
                        Annuler
                    </button>
                    <button onClick={deleteTarget?.type === "child" ? confirmRemoveChild : confirmRemoveGroup} className="button-large red-button">
                        <TrashIcon size={16} />
                        <span>Supprimer</span>
                    </button>
                </div>
            </CustomModal>
        </div>
    ) : (
        <div className="loader-container absolute">
            <CircularProgress size={30} />
        </div>
    );
};

export default Groups;
